/**
 * BOGO Cart Quantity Manager
 * Handles quantity increase/decrease for BOGO items in cart
 * - Blocks changes to FREE items
 * - Updates PAID items using line parameter
 * - Syncs FREE item quantity to match PAID item
 */

document.addEventListener('DOMContentLoaded', () => {
  const BOGO_TAG = 'bogo';
  
  // Store target quantities for BOGO items to handle race conditions
  // Key format: "variantId:soldAs" -> targetQuantity
  const targetQuantities = new Map();
  let loaderFailsafeTimeout = null; // Failsafe timeout for loader

  // ===========================
  // Loader Functions
  // ===========================
  
  function showBogoLoader() {
    const loader = document.getElementById('bogo-loader-overlay');
    if (loader) {
      loader.classList.add('active');
      
      // Clear any existing failsafe timeout
      if (loaderFailsafeTimeout) {
        clearTimeout(loaderFailsafeTimeout);
      }
      
      // Set new failsafe timeout
      loaderFailsafeTimeout = setTimeout(() => {
        hideBogoLoader();
      }, 5000);
    }
  }

  function hideBogoLoader() {
    // Clear failsafe timeout
    if (loaderFailsafeTimeout) {
      clearTimeout(loaderFailsafeTimeout);
      loaderFailsafeTimeout = null;
    }
    
    const loader = document.getElementById('bogo-loader-overlay');
    if (loader) {
      loader.classList.remove('active');
    }
  }

  // ===========================
  // Helper Functions
  // ===========================

  /**
   * Check if cart item is a FREE item (has BOGO Free discount)
   */
  function isFreeLine(cartItem) {
    const priceWrapper = cartItem.querySelector('.cart-item__price-wrapper') || 
                         cartItem.querySelector('.price');
    
    if (priceWrapper) {
      const priceText = priceWrapper.innerText.toLowerCase();
      
      // Check for $0.00 or free in price text
      if (priceText.includes('$0.00') || priceText.includes('$0,00')) {
        return true;
      }
      
      // Also check for "free" text as backup
      if (priceText.includes('free') && !priceText.includes('regular price')) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Get quantity input from cart item
   */
  function getQtyInput(cartItem) {
    return cartItem.querySelector('input[name="updates[]"]') || 
           cartItem.querySelector('.quantity__input');
  }

  /**
   * Get variant inventory from data attribute
   * @param {HTMLInputElement} input - The quantity input element
   * @returns {number|null} - Available inventory or null if not set
   */
  function getVariantInventory(input) {
    if (!input) return null;
    const inventory = input.dataset.variantInventory;
    if (inventory === undefined || inventory === null || inventory === '') return null;
    return parseInt(inventory, 10);
  }

  /**
   * Get max quantity for BOGO items (half of total inventory)
   * Since BOGO has PAID + FREE sharing the same inventory,
   * max per item = floor(totalInventory / 2)
   * @param {HTMLInputElement} input - The quantity input element
   * @returns {number|null} - Max quantity per BOGO line or null if not set
   */
  function getBogoMaxQuantity(input) {
    const totalInventory = getVariantInventory(input);
    if (totalInventory === null) return null;
    return Math.floor(totalInventory / 2);
  }

  /**
   * Get "_units_per" property value from a cart item DOM element
   * Since _units_per is a hidden property, we derive it from the visible "Sold As" text
   * @param {HTMLElement} cartItem - The cart item DOM element
   * @returns {string} - The _units_per value ("1" for single, "2" for pair)
   */
  function getUnitsPerFromDOM(cartItem) {
    // Look for the Sold As property in the item details and derive _units_per from it
    const propertyElements = cartItem.querySelectorAll('.product-option dt, .cart-item__details dt');
    for (const dt of propertyElements) {
      const labelText = dt.textContent.trim().toLowerCase();
      if (labelText.includes('sold as')) {
        const dd = dt.nextElementSibling;
        if (dd && dd.tagName === 'DD') {
          const soldAsText = dd.textContent.trim().toLowerCase();
          // If "pair" is in the text, return "2", otherwise "1"
          return soldAsText.includes('pair') ? '2' : '1';
        }
      }
    }
    return '1'; // Default to single
  }

  /**
   * Get "_units_per" property value from cart item data (from /cart.js)
   * @param {Object} item - Cart item from /cart.js response
   * @returns {string} - The _units_per value ("1" for single, "2" for pair)
   */
  function getUnitsPerFromCartItem(item) {
    if (!item || !item.properties) return '1';
    return item.properties['_units_per'] || '1';
  }

  /**
   * Create a unique group key combining variant ID and _units_per value
   * @param {string} variantId - The variant ID
   * @param {string} unitsPer - The _units_per value ("1" or "2")
   * @returns {string} - Combined key for grouping
   */
  function createGroupKey(variantId, unitsPer) {
    return `${variantId}:${unitsPer || '1'}`;
  }

  /**
   * Parse a group key back to variant ID and _units_per value
   * @param {string} groupKey - The combined group key
   * @returns {{ variantId: string, unitsPer: string }}
   */
  function parseGroupKey(groupKey) {
    const separatorIndex = groupKey.indexOf(':');
    if (separatorIndex === -1) {
      return { variantId: groupKey, unitsPer: '1' };
    }
    return {
      variantId: groupKey.substring(0, separatorIndex),
      unitsPer: groupKey.substring(separatorIndex + 1)
    };
  }

  /**
   * Group BOGO items by variant ID + Sold As property
   * This ensures items with different Sold As values are treated as separate groups
   */
  function getCartItemsGroupedByVariant(container) {
    // Try multiple selectors to find cart items
    let cartItems = Array.from(container.querySelectorAll('.cart-item'));
    
    // If no items found, try looking in table
    if (cartItems.length === 0) {
      cartItems = Array.from(container.querySelectorAll('table.cart-items .cart-item'));
    }
    
    // If still no items, try looking anywhere for tr.cart-item
    if (cartItems.length === 0) {
      cartItems = Array.from(container.querySelectorAll('tr.cart-item'));
    }
    
    const grouped = {};
    let bogoItemsFound = 0;

    cartItems.forEach((item, index) => {
      const productTags = item.dataset.productTags || '';
      const hasBOGO = productTags.split(',').some(tag => tag.trim().toLowerCase().includes(BOGO_TAG));
      
      // Skip non-BOGO items silently
      if (!hasBOGO) return;
      
      bogoItemsFound++;

      const input = getQtyInput(item);
      if (!input || !input.dataset.quantityVariantId) return;

      const variantId = input.dataset.quantityVariantId;
      const unitsPer = getUnitsPerFromDOM(item);
      const groupKey = createGroupKey(variantId, unitsPer);
      const isFree = isFreeLine(item);

      if (!grouped[groupKey]) {
        grouped[groupKey] = { paid: null, free: null, variantId: variantId, unitsPer: unitsPer };
      }

      if (isFree) {
        grouped[groupKey].free = { item, input };
      } else {
        grouped[groupKey].paid = { item, input };
      }
    });
    
    return grouped;
  }

  /**
   * Add class to FREE items (CSS will handle hiding controls)
   * Add class to PAID items (CSS will make input readonly)
   */
  function hideQuantityControlsOnFreeItems(container) {
    const groups = getCartItemsGroupedByVariant(container);
    
    // Early exit if no BOGO items found
    if (Object.keys(groups).length === 0) return;

    let markedFreeCount = 0;
    let markedPaidCount = 0;
    Object.keys(groups).forEach(groupKey => {
      const { paid, free } = groups[groupKey];
      
      // Handle FREE items - hide all controls
      if (free) {
        const { item } = free;

        // Add class to FREE item (CSS will handle the rest)
        if (!item.classList.contains('bogo-free-item')) {
          item.classList.add('bogo-free-item');
          markedFreeCount++;
        }
      }
      
      // Handle PAID items - add class to make input readonly
      if (paid) {
        const { item } = paid;
        
        // Add class to PAID item (CSS will make input readonly)
        if (!item.classList.contains('bogo-paid-item')) {
          item.classList.add('bogo-paid-item');
          markedPaidCount++;
        }
      }
    });
  }

  // ===========================
  // Reusable BOGO Helper Functions
  // ===========================

  /**
   * Find BOGO item keys (paid and free) from cart data by variant ID and _units_per value
   * @param {Object} cartData - Cart data from /cart.js
   * @param {string} variantId - The variant ID to match
   * @param {string} unitsPer - The _units_per property value to match (optional, defaults to "1")
   * @returns {{ paidItemKey: string|null, freeItemKey: string|null }}
   */
  function findBOGOItemKeys(cartData, variantId, unitsPer = '1') {
    let paidItemKey = null;
    let freeItemKey = null;
    
    cartData.items.forEach((item) => {
      const itemVariantId = String(item.variant_id);
      const itemUnitsPer = getUnitsPerFromCartItem(item);
      
      // Match both variant ID and _units_per value
      if (itemVariantId === String(variantId) && itemUnitsPer === unitsPer) {
        const isFree = item.final_price === 0 || item.price === 0;
        if (isFree) {
          freeItemKey = item.key;
        } else {
          paidItemKey = item.key;
        }
      }
    });
    
    return { paidItemKey, freeItemKey };
  }

  /**
   * Update BOGO items atomically using cart/update.js
   * @param {Object} updates - Object mapping item keys to quantities
   * @returns {Promise<Object>} - Updated cart data
   */
  async function updateCartAtomic(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    });
    return response.json();
  }

  /**
   * Refresh cart drawer UI by fetching and replacing HTML
   */
  async function refreshCartUI() {
    try {
      const [cartDrawerHtml, cartIconBubbleHtml, cartData] = await Promise.all([
        fetch('/cart?section_id=cart-drawer').then(r => r.text()),
        fetch('/cart?section_id=cart-icon-bubble').then(r => r.text()),
        fetch('/cart.js').then(r => r.json())
      ]);
      
      // Replace drawer inner
      const html = new DOMParser().parseFromString(cartDrawerHtml, 'text/html');
      const targetDrawerInner = document.querySelector('#CartDrawer .drawer__inner');
      const sourceDrawerInner = html.querySelector('#CartDrawer .drawer__inner');
      if (targetDrawerInner && sourceDrawerInner) {
        targetDrawerInner.replaceWith(sourceDrawerInner);
      }
      
      // Update cart icon bubble
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && cartIconBubbleHtml) {
        const bubbleHtml = new DOMParser().parseFromString(cartIconBubbleHtml, 'text/html');
        const sourceBubble = bubbleHtml.querySelector('.shopify-section');
        if (sourceBubble) cartIconBubble.innerHTML = sourceBubble.innerHTML;
      }
      
      // Toggle empty state
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartData.item_count === 0) {
        if (cartDrawer) cartDrawer.classList.add('is-empty');
        if (cartDrawerItems) cartDrawerItems.classList.add('is-empty');
      } else {
        if (cartDrawer) cartDrawer.classList.remove('is-empty');
        if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');
      }
      
      // Update PDP BOGO data attributes if we're on a product page
      updatePDPBogoData(cartData);
      
      // Publish event
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bogo-cart-quantity',
          cartData: cartData
        });
        publish(PUB_SUB_EVENTS.cartUpdateInMiniCart, {
          source: 'bogo-cart-quantity',
          cartData: cartData
        });
      }
      
      return cartData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update PDP BOGO data attributes when cart changes
   * This ensures the PDP add-to-cart validation uses fresh cart data
   * @param {Object} cartData - Current cart data from /cart.js
   */
  function updatePDPBogoData(cartData) {
    // Find the main product form with BOGO data attributes
    const productForm = document.querySelector('product-form[data-main-product-form][data-is-bogo="true"]');
    if (!productForm) return;
    
    // Get the variant ID from the form
    const variantInput = productForm.querySelector('input[name="id"]');
    if (!variantInput) return;
    const variantId = variantInput.value;
    
    // Calculate new cart quantity for this variant
    let cartQtyForVariant = 0;
    cartData.items.forEach(item => {
      if (String(item.variant_id) === String(variantId)) {
        cartQtyForVariant += item.quantity;
      }
    });
    
    // For BOGO: cart qty is doubled (PAID + FREE), so divide by 2
    const bogoCartPairs = Math.floor(cartQtyForVariant / 2);
    
    // Get max qty (this doesn't change, it's based on inventory)
    const bogoMaxQty = parseInt(productForm.dataset.bogoMaxQty || '0', 10);
    
    // Calculate new remaining
    const bogoRemaining = Math.max(0, bogoMaxQty - bogoCartPairs);
    
    // Update the data attributes
    productForm.dataset.bogoCartQty = bogoCartPairs;
    productForm.dataset.bogoRemaining = bogoRemaining;
  }

  /**
   * Update BOGO items and refresh UI - main reusable function
   * @param {string} variantId - Variant ID to update
   * @param {number} quantity - New quantity (0 to remove)
   * @param {string} unitsPer - The _units_per property value (optional, defaults to "1")
   */
  async function updateBOGOItems(variantId, quantity, unitsPer = '1') {
    showBogoLoader();
    
    try {
      // Get current cart to find item keys
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      const { paidItemKey, freeItemKey } = findBOGOItemKeys(cartData, variantId, unitsPer);
      
      // Build updates object
      const updates = {};
      if (paidItemKey) updates[paidItemKey] = quantity;
      if (freeItemKey) updates[freeItemKey] = quantity;
      
      // Update atomically
      await updateCartAtomic(updates);
      
      // Clear stored target quantity using the group key
      const groupKey = createGroupKey(variantId, unitsPer);
      targetQuantities.delete(groupKey);
      
      // Refresh UI
      await refreshCartUI();
      
      hideBogoLoader();
    } catch (error) {
      hideBogoLoader();
    }
  }

  /**
   * Sync BOGO items using fresh cart data (for initial load and external updates)
   */
  async function syncBOGOWithCartData(container, cartData) {
    const groups = getCartItemsGroupedByVariant(container);
    
    if (Object.keys(groups).length === 0) {
      return;
    }

    for (const groupKey of Object.keys(groups)) {
      const { paid, free, variantId, unitsPer } = groups[groupKey];
      
      if (!paid || !free) continue;

      // Get target quantity using the group key
      let targetQty;
      if (targetQuantities.has(groupKey)) {
        targetQty = targetQuantities.get(groupKey);
      } else {
        const paidLine = paid.input.dataset.index;
        const paidItemData = cartData.items[parseInt(paidLine) - 1];
        if (!paidItemData) continue;
        targetQty = paidItemData.quantity;
      }
      
      const freeLine = free.input.dataset.index;
      const freeItemData = cartData.items[parseInt(freeLine) - 1];
      if (!freeItemData) continue;
      
      const freeQty = freeItemData.quantity;
      const paidLine = paid.input.dataset.index;
      const paidItemData = cartData.items[parseInt(paidLine) - 1];
      const paidQty = paidItemData ? paidItemData.quantity : targetQty;

      // Check if sync needed
      if (targetQty === paidQty && targetQty === freeQty) {
        targetQuantities.delete(groupKey);
        continue;
      }
      
      // Use atomic update with variantId and unitsPer
      await updateBOGOItems(variantId, targetQty, unitsPer);
    }
  }

  /**
   * Sync BOGO items from DOM (fallback when cart data unavailable)
   */
  async function syncBOGO(container) {
    const groups = getCartItemsGroupedByVariant(container);
    
    if (Object.keys(groups).length === 0) return;

    for (const groupKey of Object.keys(groups)) {
      const { paid, free, variantId, unitsPer } = groups[groupKey];
      
      if (!paid || !free) continue;

      const paidQty = parseInt(paid.input.value || '0', 10);
      const freeQty = parseInt(free.input.value || '0', 10);

      if (paidQty !== freeQty) {
        await updateBOGOItems(variantId, paidQty, unitsPer);
      }
    }
  }

  /**
   * Monitor and handle cart changes
   */
  function monitorCartChanges(container) {
    // Check if there are any BOGO items before setting up monitoring
    const initialGroups = getCartItemsGroupedByVariant(container);
    const hasBOGOItems = Object.keys(initialGroups).length > 0;

    // Apply class to free items initially
    hideQuantityControlsOnFreeItems(container);
    
    // Also apply class after a short delay to catch any late-rendered items
    setTimeout(() => {
      hideQuantityControlsOnFreeItems(container);
    }, 100);

    // Listen for clicks on cart-remove-button (must be captured first, before minus/plus)
    container.addEventListener('click', (e) => {
      const removeButton = e.target.closest('cart-remove-button');
      
      if (removeButton) {
        const cartItem = removeButton.closest('.cart-item');
        
        if (!cartItem) {
          return; // No cart item found, allow normal behavior
        }

        // Check if this is a BOGO item
        const productTags = cartItem.dataset.productTags || '';
        const hasBOGO = productTags.split(',').some(tag => tag.trim().toLowerCase().includes(BOGO_TAG));
        
        if (!hasBOGO) {
          return; // Not a BOGO item, allow normal behavior
        }

        // Check if this is a FREE item
        const isFree = isFreeLine(cartItem);
        
        if (isFree) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return;
        }

        // This is a PAID BOGO item - check quantity
        const input = getQtyInput(cartItem);
        const currentQty = input ? parseInt(input.value || '0', 10) : 0;
        
        // Get variant ID and _units_per value
        const line = removeButton.dataset.index;
        const innerButton = removeButton.querySelector('button');
        const variantId = innerButton ? innerButton.dataset.variantId : null;
        const unitsPer = getUnitsPerFromDOM(cartItem);
        
        if (!line || !variantId) {
          return;
        }
        
        // Prevent default for ALL BOGO remove clicks
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        if (currentQty === 1) {
          // Removing last item - use reusable helper to remove both items
          updateBOGOItems(variantId, 0, unitsPer);
          return;
        }
        
        // Quantity > 1: Delete button should REMOVE both items entirely
        updateBOGOItems(variantId, 0, unitsPer);
        return;
      }
    }, true); // Use capture phase to intercept before cart-remove-button's handler

    // Listen for clicks on minus/plus buttons
    container.addEventListener('click', (e) => {
      const minusButton = e.target.closest('button[name="minus"]');
      const plusButton = e.target.closest('button[name="plus"]');
      
      if (!minusButton && !plusButton) {
        return; // Not a quantity button, let it pass through
      }

      const button = minusButton || plusButton;
      const cartItem = button.closest('.cart-item');
      
      if (!cartItem) {
        return; // No cart item found, let it pass through
      }

      // Check if this is a BOGO item - if not, exit immediately
      const productTags = cartItem.dataset.productTags || '';
      const hasBOGO = productTags.split(',').some(tag => tag.trim().toLowerCase().includes(BOGO_TAG));
      
      if (!hasBOGO) {
        // Not a BOGO item, allow normal Shopify behavior
        return;
      }

      // From here on, we're only dealing with BOGO items
      // Check if this is a FREE item
      const isFree = isFreeLine(cartItem);
      
      if (isFree) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // This is a PAID item, allow the change
      const input = getQtyInput(cartItem);
      const line = input ? input.dataset.index : null;
      let currentQty = input ? parseInt(input.value || '0', 10) : 0;

      if (!line) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      
      // Show loader since we're starting BOGO operations
      showBogoLoader();

      // Calculate new quantity
      let newQty;
      if (minusButton) {
        newQty = Math.max(currentQty - 1, 0);
      } else {
        newQty = currentQty + 1;
      }
      
      // Get variant ID and _units_per value to store target quantity
      const variantId = input ? input.dataset.quantityVariantId : null;
      const unitsPer = getUnitsPerFromDOM(cartItem);
      const groupKey = createGroupKey(variantId, unitsPer);
      
      // Check inventory limit for plus button (not for minus)
      // For BOGO: max per item = floor(totalInventory / 2)
      if (plusButton) {
        const bogoMaxQty = getBogoMaxQuantity(input);
        if (bogoMaxQty !== null && newQty > bogoMaxQty) {
          newQty = bogoMaxQty;
          // If already at max, don't proceed - disable button and show message
          if (currentQty >= bogoMaxQty) {
            hideBogoLoader();
            // Disable the plus button
            
            // Show error message
            const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`) || 
                                  document.getElementById(`Line-item-error-${line}`);
            if (lineItemError) {
              const errorText = lineItemError.querySelector('.cart-item__error-text');
              if (errorText) {
                errorText.textContent = window.cartStrings.maxQuantityError;
              }
              lineItemError.style.display = 'flex';
              lineItemError.style.background = 'transparent';
              lineItemError.style.color = 'black';
              lineItemError.style.border = 'none';
              lineItemError.style.paddingLeft = '0';
            }
            return;
          }
        }
      }
      
      // Special handling for quantity 0 (removal)
      if (newQty === 0) {
        if (groupKey) targetQuantities.delete(groupKey);
        updateBOGOItems(variantId, 0, unitsPer);
        return;
      }
      
      // Quantity > 0: Use reusable helper to update both items
      if (groupKey) {
        targetQuantities.set(groupKey, newQty);
        setTimeout(() => targetQuantities.delete(groupKey), 5000);
      }
      
      updateBOGOItems(variantId, newQty, unitsPer);
    }, true); // Use capture phase to intercept before other handlers

    // Listen for manual input changes - allow changes and sync FREE item to match PAID item
    // Use capture phase and stopImmediatePropagation to prevent Shopify's native handlers
    container.addEventListener('change', (e) => {
      if (e.target.matches('input[name="updates[]"]') || e.target.matches('.quantity__input')) {
        const cartItem = e.target.closest('.cart-item');
        if (cartItem) {
          const productTags = cartItem.dataset.productTags || '';
          const hasBOGO = productTags.split(',').some(tag => tag.trim().toLowerCase().includes(BOGO_TAG));
          
          if (!hasBOGO) {
            // Not a BOGO item, allow normal behavior
            return;
          }
          
          // BOGO item - STOP Shopify's native handlers immediately
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Check if this is a FREE item - block changes on FREE items
          const isFree = isFreeLine(cartItem);
          if (isFree) {
            // Restore current value for FREE items
            fetch('/cart.js')
              .then(response => response.json())
              .then(cartData => {
                const input = getQtyInput(cartItem);
                if (input && input.dataset.index) {
                  const line = parseInt(input.dataset.index) - 1;
                  const currentItem = cartData.items[line];
                  if (currentItem) {
                    input.value = currentItem.quantity;
                  }
                }
              });
            return;
          }
          
          // This is a PAID item - allow the change and sync FREE item
          const input = getQtyInput(cartItem);
          let newQty = parseInt(input.value || '0', 10);
          const line = input ? input.dataset.index : null;
          const variantId = input ? input.dataset.quantityVariantId : null;
          const unitsPer = getUnitsPerFromDOM(cartItem);
          
          if (!line || !variantId || newQty < 0) {
            return;
          }
          
          // Check inventory limit for manual input
          // For BOGO: max per item = floor(totalInventory / 2)
          const bogoMaxQty = getBogoMaxQuantity(input);
          if (bogoMaxQty !== null && newQty > bogoMaxQty) {
            newQty = bogoMaxQty;
            input.value = newQty; // Update the input to show capped value
          }
          
          // Show loader
          showBogoLoader();
          
          // Handle quantity 0 as removal
          if (newQty === 0) {
            updateBOGOItems(variantId, 0, unitsPer);
            return;
          }
          
          // Quantity > 0: Use reusable helper to update both items
          updateBOGOItems(variantId, newQty, unitsPer);
        }
      }
    }, true); // Use capture phase to intercept before Shopify's handlers

    // Monitor DOM changes (for when cart refreshes)
    const observer = new MutationObserver(() => {
      hideQuantityControlsOnFreeItems(container);
    });

    observer.observe(container, {
      childList: true,
      subtree: true
    });

    // Initial sync with fresh cart data (only if BOGO items exist)
    setTimeout(async () => {
      // Check if there are any BOGO items before syncing
      const groups = getCartItemsGroupedByVariant(container);
      if (Object.keys(groups).length === 0) {
        // No BOGO items, skip sync
        return;
      }
      
      try {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        syncBOGOWithCartData(container, cartData);
        // Ensure class is applied after sync
        setTimeout(() => {
          hideQuantityControlsOnFreeItems(container);
        }, 500);
      } catch (error) {
        // Fallback to DOM-based sync
        syncBOGO(container);
        // Ensure class is applied after fallback sync
        setTimeout(() => {
          hideQuantityControlsOnFreeItems(container);
        }, 500);
      }
    }, 500);
  }

  // Initialize for cart drawer
  let cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) {
    cartDrawer = document.querySelector('#CartDrawer-CartItems');
  }
  if (!cartDrawer) {
    cartDrawer = document.querySelector('.cart-drawer');
  }
  
  if (cartDrawer) {
    monitorCartChanges(cartDrawer);
    
    // Also listen for cart drawer opening
    const cartDrawerElement = document.querySelector('cart-drawer');
    if (cartDrawerElement) {
      // Watch for the cart drawer to become active/visible
      const observer = new MutationObserver(() => {
        if (cartDrawerElement.classList.contains('active') || 
            cartDrawerElement.classList.contains('animate')) {
          // Cart drawer opened, apply class to free items
          setTimeout(() => {
            hideQuantityControlsOnFreeItems(cartDrawer);
          }, 200);
        }
      });
      
      observer.observe(cartDrawerElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  // Initialize for cart page
  let cartPage = document.querySelector('cart-items');
  if (!cartPage) {
    cartPage = document.querySelector('form[action="/cart"]');
  }
  
  if (cartPage) {
    monitorCartChanges(cartPage);
  }

  // Listen for cart drawer opening
  document.addEventListener('cart:open', () => {
    setTimeout(() => {
      if (cartDrawer) {
        hideQuantityControlsOnFreeItems(cartDrawer);
      }
    }, 300);
  });
  
  // Listen for cart updates (when items are added/removed)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Only ignore if this is from our sync completion, not from paid item update
      if (event.source === 'bogo-cart-quantity') {
        return;
      }
      
      // If this is from our paid update, just hide controls, don't sync yet
      if (event.source === 'bogo-cart-quantity-paid-update') {
        setTimeout(() => {
          if (cartDrawer) hideQuantityControlsOnFreeItems(cartDrawer);
          if (cartPage) hideQuantityControlsOnFreeItems(cartPage);
        }, 300);
        return;
      }
      
      // Check if the update was for a BOGO item by checking productVariantId or variantId in the event
      const updatedVariantId = event.productVariantId || event.variantId;
      
      if (updatedVariantId) {
        // Fetch cart data to check if this variant is a BOGO item
        fetch('/cart.js')
          .then(response => response.json())
          .then(cartData => {
            // Find the item with this variant ID in the cart
            const cartItem = cartData.items.find(item => String(item.variant_id) === String(updatedVariantId));
            
            if (!cartItem) {
              return;
            }
            
            // Check if this item has the BOGO tag
            const productTags = cartItem.product_tags || [];
            const isBOGOItem = productTags.some(tag => tag.trim().toLowerCase().includes(BOGO_TAG));
            
            if (!isBOGOItem) {
              // Just hide controls, don't sync
              setTimeout(() => {
                if (cartDrawer) hideQuantityControlsOnFreeItems(cartDrawer);
                if (cartPage) hideQuantityControlsOnFreeItems(cartPage);
              }, 300);
              return;
            }
            
            // This is a BOGO item, proceed with syncing
            setTimeout(() => {
              if (cartDrawer) {
                const groups = getCartItemsGroupedByVariant(cartDrawer);
                if (Object.keys(groups).length > 0) {
                  hideQuantityControlsOnFreeItems(cartDrawer);
                  syncBOGOWithCartData(cartDrawer, cartData);
                }
              }
              if (cartPage) {
                const groups = getCartItemsGroupedByVariant(cartPage);
                if (Object.keys(groups).length > 0) {
                  hideQuantityControlsOnFreeItems(cartPage);
                  syncBOGOWithCartData(cartPage, cartData);
                }
              }
            }, 500);
          })
          .catch(error => {
            // Error checking variant
          });
      } else {
        // No variant ID provided - just hide controls, don't sync
        setTimeout(() => {
          if (cartDrawer) hideQuantityControlsOnFreeItems(cartDrawer);
          if (cartPage) hideQuantityControlsOnFreeItems(cartPage);
        }, 300);
      }
    });
  }
});

/**
 * Open cart drawer if URL has cart=open parameter
 */
document.addEventListener('DOMContentLoaded', () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('cart') === 'open') {
    // Wait for cart-drawer element to be available
    const checkCartDrawer = setInterval(() => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        clearInterval(checkCartDrawer);
        
        // Open the cart drawer
        cartDrawer.open();
        
        // Clean up the URL (remove ?cart=open) without page reload
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }, 100);
    
    // Timeout after 5 seconds if cart drawer not found
    setTimeout(() => {
      clearInterval(checkCartDrawer);
    }, 5000);
  }
});

